/**
 * Canonical list of upload observability event names.
 *
 * These strings are the wire format sent to PostHog / backend / admin UI, so
 * they must remain stable. When adding a new event:
 *   1. Append here (never mutate existing values)
 *   2. Add the payload shape in the JSDoc typedef block below
 *   3. Document it in docs/UPLOAD_OBSERVABILITY_REFERENCE.md#event-catalog
 *
 * Naming convention: `<subject>_<past-tense-verb>`. Namespaced by uploader
 * concept (upload_*, chunk_*, retry_*, network_*).
 */
export const UPLOAD_EVENT = Object.freeze({
  // Package lifecycle
  UPLOAD_CREATED: "upload_created",
  UPLOAD_STARTED: "upload_started",
  UPLOAD_PAUSED: "upload_paused",
  UPLOAD_RESUMED: "upload_resumed",
  UPLOAD_CANCELLED: "upload_cancelled",
  UPLOAD_COMPLETED: "upload_completed",
  UPLOAD_FAILED: "upload_failed",
  UPLOAD_TIMEOUT: "upload_timeout",
  UPLOAD_STUCK: "upload_stuck",

  // Chunk / file lifecycle (per-file, driven by tus + watchdog)
  CHUNK_STARTED: "chunk_started",
  CHUNK_COMPLETED: "chunk_completed",
  CHUNK_FAILED: "chunk_failed",

  // Retry lifecycle
  RETRY_STARTED: "retry_started",
  RETRY_COMPLETED: "retry_completed",

  // Network lifecycle
  NETWORK_OFFLINE: "network_offline",
  NETWORK_ONLINE: "network_online",
  CONNECTION_CHANGED: "connection_changed",

  // Health transitions (emitted when computeHealth() returns a new state)
  HEALTH_CHANGED: "upload_health_changed",

  // Diagnostic capture — fires when a snapshot is built
  DIAGNOSTIC_SNAPSHOT: "upload_diagnostic_snapshot",

  // API request telemetry (from the axios http bridge)
  API_REQUEST: "api_request",
  API_RESPONSE: "api_response",
  API_ERROR: "api_error",
});

/**
 * Reasons an upload can be paused. Kept in sync with backend PauseReason enum
 * so the admin dashboard renders consistent labels.
 */
export const PAUSE_REASON = Object.freeze({
  USER: "USER",
  STALL: "STALL",
  WATCHDOG: "WATCHDOG",
  OFFLINE: "OFFLINE",
  UNKNOWN: "UNKNOWN",
});

/**
 * Health states emitted by UploadHealthCalculator. Ordering roughly follows
 * severity (Healthy → Failed). The admin dashboard uses this to colour rows.
 */
export const HEALTH = Object.freeze({
  HEALTHY: "healthy",
  SLOW: "slow",
  RETRYING: "retrying",
  WAITING: "waiting",
  OFFLINE: "offline",
  POSSIBLY_STUCK: "possibly_stuck",
  FAILED: "failed",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
});

/**
 * @typedef {Object} TimelineEntry
 * @property {number} timestamp    ms since epoch
 * @property {string} event        one of UPLOAD_EVENT.*
 * @property {Record<string, any>} metadata
 */

/**
 * @typedef {Object} UploadEnvironmentSnapshot
 * @property {string} browser        e.g. "Chrome 120"
 * @property {string} os             e.g. "macOS 14"
 * @property {string} platform
 * @property {string} userAgent
 * @property {string} language
 * @property {string} timezone
 * @property {{ width: number, height: number, pixelRatio: number }} viewport
 * @property {string} frontendVersion
 * @property {string} commitSha
 */

/**
 * @typedef {Object} NetworkSnapshot
 * @property {boolean} online
 * @property {string | null} effectiveType   e.g. "4g" | "3g" | "slow-2g"
 * @property {number | null} downlink        Mbps
 * @property {number | null} rtt             ms
 * @property {boolean | null} saveData
 * @property {string} normalizedQuality      NETWORK_QUALITY enum value
 * @property {number} timestamp
 */
